/**
 * BoletosListPage
 *
 * Listagem de boletos no padrão ERPTable (Contas a Pagar):
 * toolbar (busca + filtros + período), colunas face/taxa/líquido, menu de ações.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search, Filter, Download, RefreshCw, Calendar,
  CheckCircle2, Clock, AlertCircle, FileText, X, Check, MoreVertical
} from 'lucide-react'
import { CalendarRangePicker } from '../../../../components/ERP/Calendar/Calendar'
import ERPTable from '../../../../components/ERP/ERPTable'
import { boletoService } from '../../../../services/erpApi'
import { useToast } from '../../../../hooks/useToast'
import Toast from '../../../../components/Toast/Toast'
import { formatDateOnly } from '../../../../utils/dateUtils'
import './BoletosListPage.css'
import '../ContasPagar/ContasPagarPage.css'

const STATUS_OPTIONS = [
  { id: 'WAITING', label: 'Pendente', icon: Clock },
  { id: 'PAID', label: 'Pago', icon: CheckCircle2 },
  { id: 'DECLINED', label: 'Negado', icon: AlertCircle },
  { id: 'EXPIRED', label: 'Vencido', icon: AlertCircle }
]

function BoletosListPage() {
  const navigate = useNavigate()
  const [boletos, setBoletos] = useState([])
  const [loading, setLoading] = useState(true)
  const [openMenuId, setOpenMenuId] = useState(null)
  const [menuPosition, setMenuPosition] = useState(null)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const periodBtnRef = useRef(null)
  const { toast, showToast, hideToast } = useToast()

  const [filters, setFilters] = useState({
    search: '',
    status: [],
    dueDateFrom: '',
    dueDateTo: ''
  })

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })

  const loadBoletos = useCallback(async () => {
    setLoading(true)
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        status: filters.status?.length ? filters.status : undefined,
        dueDateFrom: filters.dueDateFrom || undefined,
        dueDateTo: filters.dueDateTo || undefined
      }

      const response = await boletoService.getAll(params)
      if (response.success) {
        setBoletos(response.data || [])
        if (response.pagination) {
          setPagination((prev) => ({
            ...prev,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          }))
        }
      }
    } catch (error) {
      console.error('Erro ao carregar boletos:', error)
      showToast('Erro ao carregar boletos', 'error')
    } finally {
      setLoading(false)
    }
  }, [
    pagination.page,
    pagination.limit,
    filters.search,
    filters.status,
    filters.dueDateFrom,
    filters.dueDateTo,
    showToast
  ])

  useEffect(() => {
    loadBoletos()
  }, [loadBoletos])

  useEffect(() => {
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }))
  }, [filters.search, filters.dueDateFrom, filters.dueDateTo, filters.status])

  useEffect(() => {
    if (!openMenuId) return undefined
    const onDocClick = (e) => {
      if (
        !e.target.closest('.erp-table-row-menu-btn') &&
        !e.target.closest('.contas-pagar-dropdown-menu')
      ) {
        setOpenMenuId(null)
        setMenuPosition(null)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [openMenuId])

  const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)

  const getStatusBadge = (status) => {
    if (status === 'PAID') {
      return <span className="status-badge paid">Pago</span>
    }
    if (status === 'DECLINED') {
      return <span className="status-badge cancelled">Negado</span>
    }
    if (status === 'EXPIRED') {
      return <span className="status-badge overdue">Vencido</span>
    }
    if (status === 'WAITING') {
      return <span className="status-badge pending">Pendente</span>
    }
    return <span className="status-badge pending">{status || '—'}</span>
  }

  const hasActiveFilters = useMemo(() => {
    return (
      (Array.isArray(filters.status) && filters.status.length > 0) ||
      Boolean(filters.dueDateFrom && filters.dueDateTo)
    )
  }, [filters])

  const hasDateRangeFilter = Boolean(filters.dueDateFrom && filters.dueDateTo)

  const resetFilters = () => {
    setFilters({
      search: '',
      status: [],
      dueDateFrom: '',
      dueDateTo: ''
    })
    setIsPeriodOpen(false)
  }

  const toggleStatusFilter = (statusValue) => {
    setFilters((prev) => {
      const current = Array.isArray(prev.status) ? prev.status : []
      const isSelected = current.includes(statusValue)
      const next = isSelected
        ? current.filter((s) => s !== statusValue)
        : [...current, statusValue]
      return { ...prev, status: next }
    })
  }

  const handlePeriodRangeChange = (start, end) => {
    let from = start
    let to = end
    if (from && to && from > to) {
      ;[from, to] = [to, from]
    }
    setFilters((prev) => ({
      ...prev,
      dueDateFrom: from,
      dueDateTo: to
    }))
  }

  const handleClearPeriodFilter = () => {
    setFilters((prev) => ({
      ...prev,
      dueDateFrom: '',
      dueDateTo: ''
    }))
  }

  const handleMenuToggle = (boletoId, e) => {
    if (e) e.stopPropagation()
    if (openMenuId === boletoId) {
      setOpenMenuId(null)
      setMenuPosition(null)
      return
    }

    const rect = e.currentTarget.getBoundingClientRect()
    const menuWidth = 200
    let left = rect.right - menuWidth
    if (left < 8) left = 8

    let top = rect.bottom + 6
    const menuHeightEstimate = 280
    if (top + menuHeightEstimate > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuHeightEstimate - 6)
    }

    setMenuPosition({ top, left })
    setOpenMenuId(boletoId)
  }

  const handleDownloadPdf = async (boleto) => {
    try {
      const result = await boletoService.downloadPdf(boleto.id)
      if (!result?.success) {
        showToast(result?.message || 'PDF não disponível', 'error')
      }
    } catch (error) {
      console.error('Erro ao baixar PDF:', error)
      showToast('Erro ao baixar PDF do boleto', 'error')
    }
  }

  const handleSyncStatus = async (boleto) => {
    try {
      const response = await boletoService.syncStatus(boleto.id)
      if (response.success) {
        showToast('Status sincronizado com o PagBank', 'success')
        loadBoletos()
      } else {
        showToast(response.message || 'Falha ao sincronizar', 'error')
      }
    } catch (error) {
      console.error('Erro ao sincronizar boleto:', error)
      showToast(error.response?.data?.message || 'Erro ao sincronizar status', 'error')
    }
  }

  const handleUpdateStatus = async (boleto, status) => {
    try {
      const response = await boletoService.updateStatus(boleto.id, status)
      if (response.success) {
        const label = STATUS_OPTIONS.find((opt) => opt.id === status)?.label || status
        showToast(`Status alterado para ${label}`, 'success')
        loadBoletos()
      } else {
        showToast(response.message || 'Falha ao atualizar status', 'error')
      }
    } catch (error) {
      console.error('Erro ao atualizar status do boleto:', error)
      showToast(error.response?.data?.message || 'Erro ao atualizar status', 'error')
    }
  }

  const handleMenuAction = (action, boleto, status) => {
    setOpenMenuId(null)
    setMenuPosition(null)
    if (!boleto) return
    if (action === 'download') handleDownloadPdf(boleto)
    if (action === 'sync') handleSyncStatus(boleto)
    if (action === 'status' && status) handleUpdateStatus(boleto, status)
  }

  const resolveFee = (boleto) =>
    boleto.fee_amount != null ? Number(boleto.fee_amount) : null

  const resolveNet = (boleto) => {
    if (boleto.net_amount != null) return Number(boleto.net_amount)
    const fee = resolveFee(boleto)
    if (fee != null) return Math.max(0, Number(boleto.amount || 0) - fee)
    return null
  }

  const columns = [
    {
      key: 'customer_name',
      label: 'Cliente',
      sortable: true,
      render: (value, row) => (
        <div className="boletos-list-customer">
          <span className="boletos-list-customer-name">{value || '—'}</span>
          <span className="boletos-list-customer-id">
            {row.id ? String(row.id).substring(0, 8) : '—'}
          </span>
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Valor face',
      className: 'erp-table-num',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'fee_amount',
      label: 'Taxa',
      className: 'erp-table-num',
      render: (_value, row) => {
        const fee = resolveFee(row)
        return fee != null ? formatCurrency(fee) : '—'
      }
    },
    {
      key: 'net_amount',
      label: 'Líquido',
      className: 'erp-table-num',
      render: (_value, row) => {
        const net = resolveNet(row)
        return net != null ? formatCurrency(net) : '—'
      }
    },
    {
      key: 'due_date',
      label: 'Vencimento',
      sortable: true,
      render: (value) => formatDateOnly(value)
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'actions',
      label: '',
      headerClassName: 'erp-table-actions-col',
      align: 'center',
      className: 'erp-table-actions-col',
      render: (_value, row) => (
        <div className="erp-table-actions-cell">
          <button
            type="button"
            className={`erp-table-row-menu-btn${openMenuId === row.id ? ' is-open' : ''}`}
            onClick={(e) => handleMenuToggle(row.id, e)}
            aria-label="Ações do boleto"
            aria-expanded={openMenuId === row.id}
          >
            <MoreVertical size={16} />
          </button>
        </div>
      )
    }
  ]

  const boletoMenuAberto = openMenuId
    ? boletos.find((b) => b.id === openMenuId)
    : null

  return (
    <div className="boletos-list-page contas-pagar-page">
      <div className="page-header">
        <div>
          <h1>Boletos</h1>
          <p>Gerenciamento de cobranças emitidas</p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate('/erp/financeiro/boletos/dashboard')}
        >
          <FileText size={16} />
          Dashboard
        </button>
      </div>

      <div className="contas-pagar-toolbar">
        <div className="contas-pagar-search-wrapper">
          <Search className="contas-pagar-search-icon" size={18} />
          <input
            type="text"
            placeholder="Buscar por cliente ou ID do boleto..."
            className="contas-pagar-search-input"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
        <div className="contas-pagar-table-actions">
          <button
            type="button"
            onClick={() => setIsFilterOpen(true)}
            className={`contas-pagar-filter-button ${hasActiveFilters ? 'active' : ''}`}
          >
            <Filter size={14} /> Filtros
            {hasActiveFilters && <span className="contas-pagar-filter-badge" />}
          </button>
          <button
            ref={periodBtnRef}
            type="button"
            onClick={() => setIsPeriodOpen((prev) => !prev)}
            className={`contas-pagar-filter-button ${hasDateRangeFilter ? 'active' : ''}`}
            title="Filtrar por período de vencimento"
          >
            <Calendar size={14} /> Período
            {hasDateRangeFilter && <span className="contas-pagar-filter-badge" />}
          </button>
          <CalendarRangePicker
            open={isPeriodOpen}
            onOpenChange={setIsPeriodOpen}
            anchorRef={periodBtnRef}
            startValue={filters.dueDateFrom}
            endValue={filters.dueDateTo}
            onRangeChange={handlePeriodRangeChange}
            onClear={handleClearPeriodFilter}
            title="Vencimento"
            subtitle="Início e Fim"
          />
        </div>
      </div>

      <ERPTable
        columns={columns}
        data={boletos}
        loading={loading}
        emptyMessage="Nenhum boleto encontrado"
        loadingMessage="Carregando boletos..."
        pagination={{
          page: pagination.page,
          totalPages: pagination.totalPages || 1,
          total: pagination.total,
          showTotalCount: true,
          onPageChange: (newPage) => {
            if (newPage >= 1 && newPage <= pagination.totalPages) {
              setPagination((prev) => ({ ...prev, page: newPage }))
            }
          }
        }}
      />

      {boletoMenuAberto && menuPosition && (
        <div
          className="contas-pagar-dropdown-menu boletos-status-menu"
          style={{ top: menuPosition.top, left: menuPosition.left }}
        >
          <button type="button" onClick={() => handleMenuAction('download', boletoMenuAberto)}>
            <Download size={16} />
            Download PDF
          </button>
          <div className="boletos-status-menu-divider" role="separator" />
          <div className="boletos-status-menu-label">Alterar status</div>
          {STATUS_OPTIONS.map((opt) => {
            const isCurrent = boletoMenuAberto.status === opt.id
            return (
              <button
                key={opt.id}
                type="button"
                className={isCurrent ? 'is-current' : ''}
                disabled={isCurrent}
                onClick={() => handleMenuAction('status', boletoMenuAberto, opt.id)}
              >
                <opt.icon size={16} />
                {opt.label}
                {isCurrent && <Check size={14} className="boletos-status-menu-check" />}
              </button>
            )
          })}
          <div className="boletos-status-menu-divider" role="separator" />
          <button type="button" onClick={() => handleMenuAction('sync', boletoMenuAberto)}>
            <RefreshCw size={16} />
            Sync PagBank
          </button>
        </div>
      )}

      {isFilterOpen && (
        <>
          <div className="contas-pagar-filter-overlay" onClick={() => setIsFilterOpen(false)} />
          <aside className="contas-pagar-filter-sidebar contas-pagar-scrollbar">
            <div className="contas-pagar-filter-header">
              <div className="contas-pagar-filter-header-content">
                <div className="contas-pagar-filter-header-icon">
                  <Filter size={24} />
                </div>
                <div>
                  <h3 className="contas-pagar-filter-header-title">Filtros</h3>
                  <p className="contas-pagar-filter-header-subtitle">Refinar resultados</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsFilterOpen(false)}
                className="contas-pagar-filter-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="contas-pagar-filter-content">
              <div className="contas-pagar-filter-section">
                <h4 className="contas-pagar-filter-section-title">
                  Status (selecione múltiplos)
                </h4>
                <div className="contas-pagar-filter-options">
                  {STATUS_OPTIONS.map((opt) => {
                    const currentStatus = Array.isArray(filters.status) ? filters.status : []
                    const isSelected = currentStatus.includes(opt.id)
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => toggleStatusFilter(opt.id)}
                        className={`contas-pagar-filter-option ${isSelected ? 'selected' : ''}`}
                      >
                        <div className="contas-pagar-filter-option-content">
                          <opt.icon size={18} className="contas-pagar-filter-option-icon" />
                          <span className="contas-pagar-filter-option-label">{opt.label}</span>
                        </div>
                        {isSelected && (
                          <Check size={16} className="contas-pagar-filter-option-check" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            <div className="contas-pagar-filter-footer">
              <button type="button" className="contas-pagar-filter-reset" onClick={resetFilters}>
                Limpar filtros
              </button>
              <button
                type="button"
                className="contas-pagar-filter-apply"
                onClick={() => setIsFilterOpen(false)}
              >
                Aplicar
              </button>
            </div>
          </aside>
        </>
      )}

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </div>
  )
}

export default BoletosListPage
